import { Alert, Button, Grid, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ItemList from '../../../Common/ItemList/ItemList';
import DialogActions from '../../../Common/Dialog/DialogActions';
import DialogButton from '../../../Common/Buttons/DialogButton';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import { SelectProductListing } from '../../../UserData/_actions/Store/StoreProductListingActions';
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch';
import DialogContent from '../../../Common/Dialog/DialogContent';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import DialogTitle from '../../../Common/Dialog/DialogTitle';
import ProductListingEditCard from './ProductListingEditCard';
import type { UserProductDesignStore } from '../../../UserData/_stores/UserProductDesignStore';
import ProductSkeletonList from '../../Products/ProductSkeletonList';
import SquarespaceStorePageField from './SquarespaceStorePageField';
import usePublishListings from '../../../Common/_hooks/Store/usePublishListings';
import { StoreListingValidation } from './Utils/StoreListingValidation';
import type { StoreListingValidationError } from './Utils/StoreListingValidation';
import useStoreIntegrationName from '../../../Common/_hooks/Store/useStoreIntegrationName';
import AccountNoResults from '../../AccountNoResults';

export interface Props {
	index: number
	handleStep: (step: number) => void
	store: string
	onPublishStarted: (batchId: string) => void
	onDialogClose: () => void
	allowProductRemoval?: boolean
	backButtonLabel?: string
}

export default function EditSelectedProducts(props: Props) {
	const dispatch = useAppDispatch()
	const publishListings = usePublishListings()
	const [t] = useTranslation('account-v2')

	const listings = useAppSelector(state => state.get('UIData').get('account').get('storeProductListings'))
	const loader = useAppSelector(state => state.get('UIData').get('loaders').get('store_addProducts'))
	const userLayout = useAppSelector(state => state.get('userData').get('prefs').get('accountProductsLayout'))
	const productDesigns = useAppSelector(state => state.get('userData').get('productDesigns').get('data'))
	const products = useAppSelector(state => state.get('appData').get('products'))
	const storeLabel = useStoreIntegrationName(props.store)

	const [layout] = useState(userLayout)
	const [squarespaceStorePageId, setSquarespaceStorePageId] = useState('')

	const goToSelectProducts = useCallback(() => {
		props.handleStep(props.index - 1)
	}, [props.handleStep, props.index])

	const goToPublishProducts = useCallback(() => {
		publishListings(props.store, listings, {
			onSuccess: (response) => {
				props.onPublishStarted(response.batch_id);
				props.handleStep(props.index + 2);
			},
			squarespaceStorePageId: props.store === 'squarespace' ? squarespaceStorePageId : undefined,
		});
	}, [props.handleStep, props.index, props.onPublishStarted, props.store, listings, publishListings, squarespaceStorePageId])

	useEffect(() => {
		//If we had 0 listings, go back to Select screen
		if (props.allowProductRemoval !== false && listings.count() === 0) {
			goToSelectProducts();
		}
	}, [goToSelectProducts, listings, props.allowProductRemoval])

	const listingIds = useMemo(() => {
		return listings.keySeq().toArray();
	}, [listings]);

	const listingValidationIssues = useMemo(() => {
		const issues: Record<string, StoreListingValidationError[]> = {};
		listings.forEach((listing) => {
			const productDesignId = String(listing.get('id_product_design'));
			const productDesign = productDesigns.get(productDesignId);
			const product = products.get(String(listing.get('id_product')));
			const validation = StoreListingValidation.validate({
				store: props.store,
				listing,
				product,
				availableOptionTypes: productDesign?.get('availableOptionTypes'),
				previews: productDesign?.get('previews'),
			});

			if (validation.hasErrors) {
				issues[productDesignId] = validation.errors;
			}
		});

		return issues;
	}, [listings, productDesigns, products, props.store]);

	const listingValidationIssueList = useMemo(() => Object.values(listingValidationIssues), [listingValidationIssues]);
	const variantLimitIssueList = useMemo(() => {
		return listingValidationIssueList
			.map(errors => errors.find(error => error.code === 'store_variant_limit_exceeded'))
			.filter((error): error is StoreListingValidationError => error !== undefined);
	}, [listingValidationIssueList]);
	const firstVariantLimit = useMemo(() => variantLimitIssueList[0]?.variantLimit, [variantLimitIssueList]);
	const publishDisabled = useMemo(() => {
		return listings.count() === 0
			|| (props.store === 'squarespace' && squarespaceStorePageId === "")
			|| listingValidationIssueList.length > 0
			|| loader;
	}, [listingValidationIssueList.length, listings, loader, props.store, squarespaceStorePageId])

	const onEdit = useCallback((id: number) => {
		dispatch(SelectProductListing(String(id)))
		props.handleStep(props.index + 1)
	}, [dispatch, props.handleStep, props.index])

	const renderItem = useCallback((item: UserProductDesignStore) => {
		return <ProductListingEditCard
			item={item}
			view={layout}
			onEdit={onEdit}
			allowProductRemoval={props.allowProductRemoval}
			validationErrors={listingValidationIssues[String(item.get('id'))]}
		/>
	}, [layout, onEdit, props.allowProductRemoval, listingValidationIssues])

	const renderList = useCallback((items: React.JSX.Element | null, links: React.JSX.Element | null, loading: boolean) => {
		return <>
			<Grid container spacing={{ xs: 2, md: 3 }}>
				{loading
					? <ProductSkeletonList count={4} layout={layout} showSelection={false}/>
					: (items ?? <Grid item xs={12}>
						<AccountNoResults compact />
					</Grid>
					)
				}
			</Grid>

			{links}
		</>
	}, [t, layout])

	return <>
		<div style={{
			padding: '10px 25px 0',
			marginBottom: '-15px'
		}}>
			<Button
				variant="text"
				color="secondary"
				startIcon={<KeyboardBackspaceRoundedIcon />}
				onClick={goToSelectProducts}
			>{props.backButtonLabel ?? t('Back to Select products')}</Button>
		</div>
		<DialogTitle>{t('Edit Selected products')}</DialogTitle>
		<DialogContent>
			<Stack spacing={3}>
				<Typography variant="body1">{t('Edit details and pricing of your listings before publishing them to your store.')}</Typography>

				{props.store === 'squarespace' ? <SquarespaceStorePageField
					value={squarespaceStorePageId}
					onChange={setSquarespaceStorePageId}
				/> : null}

				{firstVariantLimit ? <Alert severity="error">
					{firstVariantLimit.requiresCompleteOptionMatrix
						? t('{{store}} allows up to {{max}} variation rows per listing. Please edit {{count}} selected listing(s) to remove options before publishing.', {
							store: storeLabel,
							max: firstVariantLimit.max,
							count: variantLimitIssueList.length,
						})
						: t('{{store}} allows up to {{max}} variants per listing. Edit {{count}} selected listing(s) to remove options before publishing.', {
							store: storeLabel,
							max: firstVariantLimit.max,
							count: variantLimitIssueList.length,
						})}
				</Alert> : null}

				{variantLimitIssueList.length === 0 && listingValidationIssueList.length > 0 ? <Alert severity="error">
					{t('Please edit {{count}} selected listing(s) before publishing.', {
						count: listingValidationIssueList.length,
					})}
				</Alert> : null}

				<div>
					<ItemList
						controlled
						dataType={'productDesigns'}
						ids={listingIds}
						limit={9999}
						item={renderItem}
						render={renderList}
					/>
				</div>
			</Stack>
		</DialogContent>

		<DialogActions>
			<DialogButton
				color="secondary"
				onClick={props.onDialogClose}
			>{t('Cancel')}</DialogButton>

			<DialogButton
				color="accent"
				onClick={goToPublishProducts}
				disabled={publishDisabled}
			>{t('Publish {{count}} Products', { count: listings.count() })}</DialogButton>
		</DialogActions>
	</>;
}
